import React from 'react';
import __ from "lodash";
import {Tooltip, Table, TableHead, TableRow, TableCell, TableBody, Grid, Checkbox} from "@material-ui/core";
import { LabelledPaper, MaskedNumber } from '../../../controls';
import {defaultCostFilters, costTypes, totalFields, getGrandTotals} from "../includes/costingDialogFunctions";

const LabelledBox = (props) => {
  return (
    <LabelledPaper 
      checkChildren={true}
      style={{display: "block", border: "solid 1px #b9b9b9" }}
      labelStyle={{background: "#f0f0f0"}}
      collapsible={false} 
      {...__.omit(props, ['children'])}
    >
      {props.children}
    </LabelledPaper>
  )
}

const SectionHeading = (props) => {
  const { children, ...other } = props;
  return (
    <div style={{marginTop: 10, marginBottom: 2, textAlign: "left", fontSize: "14px", fontWeight: "bold", ...other}}>
      {children}
    </div>
  );
}

const LineTotal = (props) => {
  const {sectors, costType, costFilters} = props;
  let total = 0;
  for (let i = 0; i < sectors.length; i++) {
    const sector = sectors[i];    
    if (costFilters['flightCostDetails'][costType.code][i]) {
      let cost = totalFields(costType, sector, i, costFilters) || 0;
      total += cost;
    }
  }
  if (total) return (<MaskedNumber displayType="text" value={total} prefix="$" decimalScale={2} fixedDecimalScale={true} />);
  return (<span>--</span>);
}

const FlightCostsView = (props) => {
  const {record, classes, defaultValues, weightUom, readOnly, filtersEnabled, onChange } = props;

  const legs = record?.legs || [];
  const sectors = __.filter(legs, {transportType: 'PRIMARY'});

  const costFilters = {
    ...defaultCostFilters,
    ...(record?.costFilters || {})
  };

  const totals = getGrandTotals(sectors, costTypes, costFilters);

  const toggleFilter = (code, value) => {
    const changes = {flightCostDetails: {...costFilters.flightCostDetails}};
    changes[code] = value;
    for (let i = 0; i < changes.flightCostDetails[code].length; i++) {
      changes.flightCostDetails[code][i] = value;
    }
    onChange(changes);
  }
  const toggleSectorFilter = (sector, value) => {
    const changes = {FlightSectors: [...costFilters.FlightSectors], flightCostDetails: {...costFilters.flightCostDetails}};
    changes['FlightSectors'][sector] = value;

    for (const costType of costTypes) {
      const {code, canEdit} = costType;
      if (!canEdit) continue;
      changes.flightCostDetails[code][sector] = value;
    }
    onChange(changes);
  }
  const toggleIndividualFilter = (code, sector, value) => {
    const changes = {flightCostDetails: {...costFilters.flightCostDetails}};
    changes['flightCostDetails'][code][sector] = value;
    onChange(changes);
  }
  const toggleAllFilters = (event) => {
    const changes = {};
    for (const costType of costTypes) {
      const {code, canEdit} = costType;
      if (!canEdit) continue;
      changes[code] = event.target.checked;
      toggleFilter(code, event.target.checked);
    }
    onChange(changes);
  }

  //get true if all editable filters are enabled or false if all false or null if mixed
  const allFilterState = () => {
    let filterState = undefined;
    for (const costType of costTypes) {
      const {code, canEdit} = costType;
      if (!canEdit) continue;
      for (let i = 0; i < costFilters['flightCostDetails'][code].length; i++) {
        if (filterState === undefined) filterState = costFilters['flightCostDetails'][code][i];
        if (filterState !== costFilters['flightCostDetails'][code][i]) filterState = null;
      }
    }
    return {checked: filterState===true, indeterminate: filterState===null};
  }

  const codeFilterState = (code) => {
    let filterState = undefined;
    const costType=__.find(costTypes, {code: code});
    if (!costType) return {checked: true, indeterminate: false};
    const {canEdit} = costType;
    if (!canEdit) return {checked: true, indeterminate: false};
    for (let i = 0; i < costFilters['flightCostDetails'][code].length; i++) {
      if (filterState === undefined) filterState = costFilters['flightCostDetails'][code][i];
      if (filterState !== costFilters['flightCostDetails'][code][i]) filterState = null;
    }
    return {checked: filterState===true, indeterminate: filterState===null};
  }

  const sectorFilterState = (sector) => {
    let filterState = undefined;
    for (const costType of costTypes) {
      const {code, canEdit} = costType;
      if (!canEdit) continue;
      if (filterState === undefined) filterState = costFilters['flightCostDetails'][code][sector];
      if (filterState !== costFilters['flightCostDetails'][code][sector]) filterState = null;
    }
    return {checked: filterState===true, indeterminate: filterState===null};
  }

  return (
    <div key="FlightCostsContainer" style={{width: "100%", textAlign:"left" }}>
      <Grid containerspacing={3}>
        <Grid item xs={12} row>
          <LabelledBox label="" >
            <div style={{padding: 8}}>
              <SectionHeading>Flight Costs</SectionHeading>
              <Table className={classes.flightCosts}>
                <TableHead>
                  <TableRow>
                    <TableCell style={{}}>{filtersEnabled && <div style={{display: 'inline-block', width: 28, height: 16}}><Checkbox style={{width: 16, height: 16, padding: 0}} {...allFilterState()} onChange={toggleAllFilters}/></div>}<div style={{display: 'inline-block', height: 16, verticalAlign: 'top'}}>Cost Type</div></TableCell>
                    {sectors.map((sector, i) => (
                      <TableCell width="100px">
                        <div>
                          <div style={{display: 'inline-block', width: 20, height: 16, float: 'left'}}>{filtersEnabled && <Checkbox style={{width: 16, height: 16, padding: 0}} {...sectorFilterState(i)} onChange={(event) => toggleSectorFilter(i, event.target.checked)}/>}</div>
                          <div style={{display: 'inline-block', width: 80, height: 16, verticalAlign: 'top', textAlign: "center"}}>
                            {sector.originAirportCode} - {sector.destinationAirportCode}
                          </div>
                        </div>
                      </TableCell>
                    ))}
                    <TableCell width="100px" align="right">Totals</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {costTypes.map((costType) => {
                    return (
                      <TableRow>
                        <TableCell style={{fontWeight: 700}}>{filtersEnabled && <div style={{display: 'inline-block', width: 28, height: 16}}>{filtersEnabled && <Checkbox style={{width: 16, height: 16, padding: 0, color: !costType.canEdit?"rgba(0,0,0,0.34)":"rgba(0,0,0,0.0.87)"}} disabled={!costType.canEdit} {...codeFilterState(costType.code)} code={costType.code} onChange={(e) => toggleFilter(costType.code, e.target.checked)}/>}</div>}<div style={{display: 'inline-block', height: 16, verticalAlign: 'top'}}>{costType.name}</div></TableCell>
                        {sectors.map((sector, i) => {
                          let color = "rgba(0,0,0,1)";
                          let color2 = "rgba(0,0,0,0.87)";
                          if (costFilters['flightCostDetails'][costType.code][i] === false) {
                            color = "rgba(0,0,0,0.34)";
                            color2 = "rgba(0,0,0,0.34)";
                          }
                          if (sector.jetPlanStatus === 'COMPLETED') {
                            if (sector[costType.fieldName] || Array.isArray(costType.fieldName)) {
                              if (typeof costType.tooltipMethod === 'function') {
                                return (
                                  <TableCell style={{color: i%2===0?color:color2, backgroundColor: i%2===0?"#f0f0f0":"#ffffff"}} width="100px">
                                    <Tooltip classes={{tooltip: classes.costingTooltip}} title={costType.tooltipMethod(sector, i, record)}>
                                      <div>
                                        <div style={{display: 'inline-block', width: 20, height: 16, float: 'left'}}>{filtersEnabled && <Checkbox style={{width: 16, height: 16, padding: 0}} checked={costFilters['flightCostDetails'][costType.code][i] === true} code={costType.code} disabled={!costType.canEdit} onChange={(e) => toggleIndividualFilter(costType.code, i, e.target.checked)}/>}</div>
                                        <div style={{display: 'inline-block', width: 80, height: 16, verticalAlign: 'top', textAlign: "right"}}>
                                          <MaskedNumber id={`cost-${costType.code}-${i}`} displayType="text" value={totalFields(costType, sector, i, costFilters)} prefix="$" decimalScale={2} fixedDecimalScale={true} />
                                        </div>
                                      </div>
                                    </Tooltip>
                                  </TableCell>
                                )
                              }
                              return (
                                <TableCell style={{backgroundColor: i%2===0?"#f0f0f0":"#ffffff"}} width="100px">
                                  <div>
                                    <div style={{display: 'inline-block', width: 20, height: 16, float: 'left'}}>{filtersEnabled && <Checkbox style={{width: 16, height: 16, padding: 0}} checked={costFilters['flightCostDetails'][costType.code][i] === true} code={costType.code} onChange={(e) => toggleIndividualFilter(costType.code, i, e.target.checked)}/>}</div>
                                    <div style={{display: 'inline-block', width: 80, height: 16, verticalAlign: 'top', textAlign: "right"}}>
                                      <MaskedNumber displayType="text" value={totalFields(costType, sector, i, costFilters)} prefix="$" decimalScale={2} fixedDecimalScale={true} />
                                    </div>
                                  </div>
                                </TableCell>
                              )
                            };
                            return (
                              <TableCell style={{backgroundColor: i%2===0?"#f0f0f0":"#ffffff"}} width="100px" align="right"><span>--</span></TableCell>
                            )
                          }
                          return <TableCell style={{backgroundColor: i%2===0?"#f0f0f0":"#ffffff"}} width="100px" align="right"><span>waiting</span></TableCell>
                        })}
                        <TableCell style={{backgroundColor: "#d9d9d9", fontWeight: 700}} width="100px" align="right"><LineTotal sectors={sectors} costType={costType} costFilters={costFilters} /></TableCell>
                      </TableRow>
                    )
                  }
                  )}
                  <TableRow>
                    <TableCell style={{backgroundColor: "#f0f0f0", fontWeight: 700}}>Sector Totals</TableCell>
                    {totals.map((total, i) => (
                      <TableCell style={{backgroundColor: i === (totals.length - 1)?"#c9c9c9":i%2===0?"#d0d0d0":"#d9d9d9", fontWeight: 700}} width="100px" align="right">{total?<MaskedNumber displayType="text" value={total} prefix="$" decimalScale={2} fixedDecimalScale={true} />:<span>-</span>}</TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </LabelledBox>
        </Grid>
      </Grid>
    </div>
  )
}

export default FlightCostsView;